import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { alpha } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';

// ─── TOKENS (from your design system) ────────────────────────────
const ACCENT   = '#38bdf8';
const ACCENT_2 = '#3b82f6';
const ACCENT_3 = '#818cf8';
const CARD     = '#111827';
const BG_TOP   = '#0f172a';
const TEXT_PRIMARY   = '#f8fafc';
const TEXT_SECONDARY = '#cbd5e1';
const RADIUS_PILL = '999px';
const RADIUS_MD = '12px';
const RADIUS_LG = '16px';
const INPUT_BG = alpha('#1e293b', 0.65);
const INPUT_BORDER = alpha('#94a3b8', 0.22);

// Accent cycle per filter column (sky → blue → violet → back)
const ACCENT_CYCLE = [ACCENT, ACCENT_2, ACCENT_3, '#a78bfa'];

// ─── SPRING PRESETS ──────────────────────────────────────────────
const SPRING_SNAPPY  = { type: 'spring', stiffness: 420, damping: 26 };
const SPRING_BOUNCY  = { type: 'spring', stiffness: 340, damping: 20 };
const EASE_OUT_CUBIC = { duration: 0.22, ease: [0.33, 1, 0.68, 1] };

// ─── GLOW CHIP ───────────────────────────────────────────────────
const GlowChip = ({ label, onDelete, color = ACCENT }) => (
  <motion.span
    layout
    initial={{ opacity: 0, scale: 0.7, y: 4 }}
    animate={{ opacity: 1, scale: 1, y: 0 }}
    exit={{ opacity: 0, scale: 0.6, y: -4 }}
    transition={SPRING_BOUNCY}
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
      padding: '3px 10px 3px 12px',
      borderRadius: 999,
      fontSize: '0.72rem',
      fontWeight: 700,
      letterSpacing: '0.03em',
      color,
      background: `linear-gradient(135deg, ${alpha(color, 0.18)}, ${alpha(color, 0.06)})`,
      border: `1px solid ${alpha(color, 0.38)}`,
      boxShadow: `0 0 10px ${alpha(color, 0.2)}`,
      cursor: 'default',
      userSelect: 'none',
      whiteSpace: 'nowrap',
    }}
  >
    {label}
    <motion.button
      whileHover={{ scale: 1.25, rotate: 90 }}
      whileTap={{ scale: 0.85 }}
      transition={SPRING_SNAPPY}
      onClick={onDelete}
      style={{
        all: 'unset',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 16,
        height: 16,
        borderRadius: '50%',
        background: alpha(color, 0.22),
        cursor: 'pointer',
        color,
        fontSize: 11,
        fontWeight: 900,
        lineHeight: 1,
      }}
    >
      ×
    </motion.button>
  </motion.span>
);

// ─── PORTAL DROPDOWN ─────────────────────────────────────────────
// Renders directly into document.body so no parent overflow/z-index
// stacking context can ever clip it.
const PortalDropdown = ({ triggerRect, accentColor, value, options, onSelect, onClose, searchable = true }) => {
  const [query, setQuery] = useState('');
  const DROPDOWN_MAX_H = 320;
  const DROPDOWN_W = Math.max(triggerRect.width, 180);

  const filteredOptions = searchable && query.trim()
    ? options.filter((opt) => opt.label.toLowerCase().includes(query.trim().toLowerCase()))
    : options;

  // Decide whether to open downward or upward
  const spaceBelow = window.innerHeight - triggerRect.bottom;
  const openUp = spaceBelow < DROPDOWN_MAX_H + 12 && triggerRect.top > DROPDOWN_MAX_H;

  const top = openUp
    ? triggerRect.top + window.scrollY - 6  // will be shifted up via translateY(-100%)
    : triggerRect.bottom + window.scrollY + 6;

  return ReactDOM.createPortal(
    <>
      {/* Full-screen invisible backdrop */}
      <div
        onClick={() => { setQuery(''); onClose(); }}
        style={{ position: 'fixed', inset: 0, zIndex: 9998 }}
      />
      <motion.div
        initial={{ opacity: 0, y: openUp ? 8 : -8, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: openUp ? 6 : -6, scale: 0.97 }}
        transition={SPRING_SNAPPY}
        style={{
          position: 'absolute',
          top,
          left: triggerRect.left + window.scrollX,
          width: DROPDOWN_W,
          zIndex: 9999,
          transform: openUp ? 'translateY(-100%)' : undefined,
          background: `linear-gradient(160deg, ${alpha('#1e293b', 0.99)}, ${alpha(CARD, 0.99)})`,
          border: `1px solid ${alpha(accentColor, 0.32)}`,
          borderRadius: RADIUS_MD,
          boxShadow: `0 24px 60px ${alpha('#000', 0.6)}, 0 0 0 1px ${alpha(accentColor, 0.1)}, 0 0 28px ${alpha(accentColor, 0.12)}`,
          overflow: 'hidden',
          backdropFilter: 'blur(16px)',
          maxHeight: DROPDOWN_MAX_H,
          overflowY: 'auto',
        }}
      >
        {searchable && (
          <div style={{ padding: '8px 10px 6px', borderBottom: `1px solid ${alpha(accentColor, 0.12)}` }}>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Type to search…"
              autoFocus
              onClick={(e) => e.stopPropagation()}
              style={{
                width: '100%',
                boxSizing: 'border-box',
                height: 32,
                borderRadius: RADIUS_MD,
                border: `1px solid ${alpha(accentColor, 0.28)}`,
                background: alpha(BG_TOP, 0.85),
                color: TEXT_PRIMARY,
                fontSize: '0.8rem',
                padding: '0 10px',
                outline: 'none',
              }}
            />
          </div>
        )}
        <DropdownItem
          label="None"
          selected={value === ''}
          accentColor={accentColor}
          onClick={() => { setQuery(''); onSelect(''); }}
          index={0}
        />
        {filteredOptions.length === 0 ? (
          <div style={{ padding: '12px 14px', fontSize: '0.78rem', color: alpha(TEXT_SECONDARY, 0.55) }}>
            No matches
          </div>
        ) : (
          filteredOptions.map((opt, i) => (
            <DropdownItem
              key={opt.value}
              label={opt.label}
              selected={value === opt.value}
              accentColor={accentColor}
              onClick={() => { setQuery(''); onSelect(opt.value); }}
              index={i + 1}
            />
          ))
        )}
      </motion.div>
    </>,
    document.body
  );
};

// ─── CUSTOM SELECT ───────────────────────────────────────────────
const CustomSelect = ({ name, value, options, onChange, placeholder, accentColor, searchable }) => {
  const enableSearch = searchable ?? options.length >= 4;
  const [open, setOpen] = useState(false);
  const [triggerRect, setTriggerRect] = useState(null);
  const triggerRef = useRef(null);

  const selected = options.find(o => o.value === value);

  const handleOpen = () => {
    if (triggerRef.current) {
      setTriggerRect(triggerRef.current.getBoundingClientRect());
    }
    setOpen((p) => !p);
  };

  const handleSelect = (val) => {
    onChange(name, val);
    setOpen(false);
  };

  // Recompute position on scroll/resize while open
  useEffect(() => {
    if (!open) return;
    const update = () => {
      if (triggerRef.current) setTriggerRect(triggerRef.current.getBoundingClientRect());
    };
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [open]);

  return (
    <div style={{ position: 'relative' }}>
      <motion.button
        ref={triggerRef}
        whileTap={{ scale: 0.97 }}
        onClick={handleOpen}
        style={{
          all: 'unset',
          width: '100%',
          boxSizing: 'border-box',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '8px 12px',
          borderRadius: RADIUS_MD,
          border: `1px solid ${open ? alpha(accentColor, 0.55) : alpha(accentColor, 0.22)}`,
          background: open
            ? `linear-gradient(135deg, ${alpha(accentColor, 0.1)}, ${alpha(CARD, 0.9)})`
            : alpha(BG_TOP, 0.7),
          color: selected ? TEXT_PRIMARY : alpha(TEXT_SECONDARY, 0.5),
          fontSize: '0.82rem',
          fontWeight: selected ? 600 : 400,
          cursor: 'pointer',
          boxShadow: open ? `0 0 0 2px ${alpha(accentColor, 0.2)}, 0 0 16px ${alpha(accentColor, 0.12)}` : 'none',
          transition: 'all 0.18s ease',
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {selected?.label || placeholder}
        </span>
        <motion.span
          animate={{ rotate: open ? 180 : 0 }}
          transition={EASE_OUT_CUBIC}
          style={{ marginLeft: 8, fontSize: 10, color: accentColor, flexShrink: 0 }}
        >
          ▾
        </motion.span>
      </motion.button>

      <AnimatePresence>
        {open && triggerRect && (
          <PortalDropdown
            triggerRect={triggerRect}
            accentColor={accentColor}
            value={value}
            options={options}
            onSelect={handleSelect}
            onClose={() => setOpen(false)}
            searchable={enableSearch}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export const isActiveFilterValue = (value) => {
  if (value === null || value === undefined || value === '') return false;
  if (value === 'all') return false;
  return true;
};

export const countActiveFilters = (filters) =>
  Object.values(filters || {}).filter(isActiveFilterValue).length;

const DateFilterInput = ({ name, value, onChange, label, accentColor }) => {
  const today = new Date().toISOString().slice(0, 10);
  return (
    <input
      type="date"
      name={name}
      value={value || ''}
      max={today}
      onChange={(e) => onChange(name, e.target.value)}
      style={{
        width: '100%',
        boxSizing: 'border-box',
        height: 40,
        borderRadius: RADIUS_MD,
        border: `1px solid ${alpha(accentColor, 0.28)}`,
        background: alpha(BG_TOP, 0.85),
        color: TEXT_PRIMARY,
        fontSize: '0.82rem',
        padding: '0 12px',
        outline: 'none',
        fontFamily: "'DM Sans', sans-serif",
        colorScheme: 'dark',
      }}
    />
  );
};

const NumberFilterInput = ({ name, value, onChange, accentColor, min, max, step, placeholder }) => (
  <input
    type="number"
    name={name}
    value={value ?? ''}
    min={min}
    max={max}
    step={step ?? 'any'}
    placeholder={placeholder}
    onChange={(e) => onChange(name, e.target.value)}
    style={{
      width: '100%',
      boxSizing: 'border-box',
      height: 40,
      borderRadius: RADIUS_MD,
      border: `1px solid ${alpha(accentColor, 0.28)}`,
      background: alpha(BG_TOP, 0.85),
      color: TEXT_PRIMARY,
      fontSize: '0.82rem',
      padding: '0 12px',
      outline: 'none',
      fontFamily: "'DM Sans', sans-serif",
    }}
  />
);

const DropdownItem = ({ label, selected, accentColor, onClick, index }) => (
  <motion.div
    initial={{ opacity: 0, x: -6 }}
    animate={{ opacity: 1, x: 0 }}
    transition={{ ...EASE_OUT_CUBIC, delay: index * 0.03 }}
    onClick={onClick}
    whileHover={{ x: 4, background: alpha(accentColor, 0.12) }}
    style={{
      padding: '9px 14px',
      fontSize: '0.82rem',
      fontWeight: selected ? 700 : 400,
      color: selected ? accentColor : TEXT_SECONDARY,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      borderLeft: selected ? `3px solid ${accentColor}` : '3px solid transparent',
      background: selected ? alpha(accentColor, 0.1) : 'transparent',
      transition: 'background 0.12s ease',
    }}
  >
    {selected && (
      <motion.span
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={SPRING_BOUNCY}
        style={{ fontSize: 11, color: accentColor }}
      >
        ✓
      </motion.span>
    )}
    {label}
  </motion.div>
);

// ─── SEARCH INPUT (debounced) ────────────────────────────────────
// `value` / `onChange` are the parent's search state (fires after delay).
// Internal `inputValue` updates instantly so the input feels responsive.
const DEBOUNCE_MS = 350;

const SearchInput = ({ value, onChange, debounceMs = DEBOUNCE_MS, placeholder = 'Search...' }) => {
  const [focused,    setFocused]    = useState(false);
  const [inputValue, setInputValue] = useState(value);
  const [isPending,  setIsPending]  = useState(false);
  const timerRef    = useRef(null);
  // Tracks the last value we sent upstream, so we can tell the difference
  // between "parent echoed our own debounced update" vs "parent reset us
  // externally (e.g. Clear All)".
  const lastSentRef = useRef(value);

  useEffect(() => {
    // Only snap input to parent value when it's a genuine external change,
    // not when the parent is simply reflecting the debounced value we sent.
    if (value !== lastSentRef.current) {
      lastSentRef.current = value;
      setInputValue(value);
      setIsPending(false);
      clearTimeout(timerRef.current);
    }
  }, [value]);

  const handleChange = (raw) => {
    setInputValue(raw);
    setIsPending(true);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      lastSentRef.current = raw;
      onChange(raw);
      setIsPending(false);
    }, debounceMs);
  };

  const handleClear = () => {
    clearTimeout(timerRef.current);
    lastSentRef.current = '';
    setInputValue('');
    setIsPending(false);
    onChange('');
  };

  // Flush immediately on Enter so users can confirm without waiting
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      clearTimeout(timerRef.current);
      lastSentRef.current = inputValue;
      onChange(inputValue);
      setIsPending(false);
    }
  };

  useEffect(() => () => clearTimeout(timerRef.current), []);

  return (
    <div
      style={{
        position: 'relative',
        flex: 1,
        minWidth: 0,
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <div
        style={{
          position: 'relative',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          height: 40,
          borderRadius: RADIUS_PILL,
          border: `1px solid ${focused ? alpha(ACCENT, 0.45) : INPUT_BORDER}`,
          background: INPUT_BG,
          transition: 'border-color 0.18s ease, box-shadow 0.18s ease',
          boxShadow: focused ? `0 0 0 2px ${alpha(ACCENT, 0.12)}` : 'none',
          overflow: 'hidden',
          zIndex: 1,
        }}
      >
        {/* Search / spinner icon */}
        <motion.span
          animate={{ color: focused ? ACCENT : alpha(TEXT_SECONDARY, 0.5) }}
          style={{
            display: 'flex',
            alignItems: 'center',
            paddingLeft: 12,
            fontSize: 16,
            flexShrink: 0,
          }}
        >
          <AnimatePresence mode="wait">
            {isPending ? (
              // Spinning arc while debounce timer is running
              <motion.span
                key="spinner"
                initial={{ opacity: 0, rotate: 0 }}
                animate={{ opacity: 1, rotate: 360 }}
                exit={{ opacity: 0 }}
                transition={{ rotate: { duration: 0.7, repeat: Infinity, ease: 'linear' }, opacity: { duration: 0.15 } }}
                style={{ display: 'flex', alignItems: 'center' }}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                  <path d="M8 2 A6 6 0 0 1 14 8" />
                </svg>
              </motion.span>
            ) : (
              <motion.span
                key="icon"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.15 }}
                style={{ display: 'flex', alignItems: 'center' }}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                  <circle cx="6.5" cy="6.5" r="4.5" />
                  <line x1="10.5" y1="10.5" x2="14" y2="14" />
                </svg>
              </motion.span>
            )}
          </AnimatePresence>
        </motion.span>

        <input
          type="text"
          value={inputValue}
          onChange={e => handleChange(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          placeholder={placeholder}
          style={{
            all: 'unset',
            flex: 1,
            minWidth: 0,
            padding: '9px 10px',
            fontSize: '0.875rem',
            color: TEXT_PRIMARY,
            caretColor: ACCENT,
          }}
        />

        <AnimatePresence>
          {inputValue && (
            <motion.button
              initial={{ opacity: 0, scale: 0.5, rotate: -90 }}
              animate={{ opacity: 1, scale: 1, rotate: 0 }}
              exit={{ opacity: 0, scale: 0.5, rotate: 90 }}
              transition={SPRING_SNAPPY}
              whileHover={{ scale: 1.15 }}
              whileTap={{ scale: 0.85 }}
              onClick={handleClear}
              style={{
                all: 'unset',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 28,
                height: 28,
                marginRight: 6,
                borderRadius: '50%',
                background: alpha(ACCENT, 0.12),
                color: alpha(TEXT_SECONDARY, 0.8),
                fontSize: 14,
                cursor: 'pointer',
                flexShrink: 0,
              }}
            >
              ×
            </motion.button>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

// ─── FILTER TOGGLE BUTTON ────────────────────────────────────────
const FilterToggle = ({ onClick, active, activeCount }) => (
  <motion.button
    whileHover={{ scale: 1.02 }}
    whileTap={{ scale: 0.97 }}
    onClick={onClick}
    style={{
      all: 'unset',
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      gap: 6,
      height: 40,
      padding: '0 16px',
      borderRadius: RADIUS_PILL,
      border: `1px solid ${active ? alpha(ACCENT, 0.45) : INPUT_BORDER}`,
      background: active ? alpha(ACCENT, 0.1) : INPUT_BG,
      color: active ? ACCENT : alpha(TEXT_SECONDARY, 0.85),
      fontSize: '0.82rem',
      fontWeight: 600,
      cursor: 'pointer',
      whiteSpace: 'nowrap',
      flex: '0 0 auto',
      justifyContent: 'center',
      minWidth: 132,
      transition: 'all 0.18s ease',
    }}
  >
    {/* Tune icon */}
    <svg width="15" height="15" viewBox="0 0 15 15" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round">
      <line x1="2" y1="4" x2="13" y2="4" />
      <line x1="2" y1="7.5" x2="13" y2="7.5" />
      <line x1="2" y1="11" x2="13" y2="11" />
      <circle cx="5" cy="4" r="1.5" fill="currentColor" stroke="none" />
      <circle cx="10" cy="7.5" r="1.5" fill="currentColor" stroke="none" />
      <circle cx="6" cy="11" r="1.5" fill="currentColor" stroke="none" />
    </svg>
    Filters

    <AnimatePresence>
      {activeCount > 0 && (
        <motion.span
          key="badge"
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0, opacity: 0 }}
          transition={SPRING_BOUNCY}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: 18,
            height: 18,
            padding: '0 5px',
            borderRadius: 999,
            background: `linear-gradient(135deg, ${ACCENT}, ${ACCENT_2})`,
            color: '#0b1220',
            fontSize: '0.68rem',
            fontWeight: 800,
            boxShadow: `0 0 8px ${alpha(ACCENT, 0.5)}`,
          }}
        >
          {activeCount}
        </motion.span>
      )}
    </AnimatePresence>
  </motion.button>
);

// ─── FILTER PANEL ────────────────────────────────────────────────
const FilterPanel = ({ filterOptions, filters, onFilterChange, onClearFilters, activeCount, customFilters }) => (
  <motion.div
    key="filter-panel"
    initial={{ opacity: 0, y: -12, scale: 0.98 }}
    animate={{ opacity: 1, y: 0, scale: 1 }}
    exit={{ opacity: 0, y: -8, scale: 0.99 }}
    transition={SPRING_BOUNCY}
    style={{
      marginTop: 12,
      padding: 'clamp(14px, 3vw, 20px)',
      borderRadius: RADIUS_LG,
      background: `linear-gradient(145deg, ${alpha('#1e293b', 0.95)}, ${alpha(CARD, 0.92)})`,
      border: `1px solid ${alpha(ACCENT_2, 0.2)}`,
      boxShadow: `0 20px 50px ${alpha('#000', 0.45)}, 0 0 0 1px ${alpha(ACCENT, 0.06)}, 0 0 30px ${alpha(ACCENT_2, 0.08)}`,
      backdropFilter: 'blur(16px)',
    }}
  >
    {/* Panel header */}
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
      <span style={{ fontSize: '0.8rem', fontWeight: 700, color: alpha(TEXT_SECONDARY, 0.7), letterSpacing: '0.08em', textTransform: 'uppercase' }}>
        Filter options
      </span>
      {activeCount > 0 && (
        <motion.button
          whileHover={{ scale: 1.04 }}
          whileTap={{ scale: 0.95 }}
          onClick={onClearFilters}
          initial={{ opacity: 0, x: 8 }}
          animate={{ opacity: 1, x: 0 }}
          style={{
            all: 'unset',
            display: 'flex',
            alignItems: 'center',
            gap: 5,
            padding: '4px 10px',
            borderRadius: 999,
            border: `1px solid ${alpha('#f87171', 0.35)}`,
            color: '#f87171',
            fontSize: '0.72rem',
            fontWeight: 700,
            cursor: 'pointer',
            background: alpha('#f87171', 0.07),
            transition: 'background 0.15s',
          }}
        >
          ✕ Clear all
        </motion.button>
      )}
    </div>

    {/* Filter columns */}
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 160px), 1fr))',
        gap: 16,
      }}
    >
      {filterOptions.map((option, i) => {
        const color = ACCENT_CYCLE[i % ACCENT_CYCLE.length];
        const fieldLabel = (
          <div
            style={{
              fontSize: '0.72rem',
              fontWeight: 700,
              letterSpacing: '0.06em',
              color,
              textTransform: 'uppercase',
              marginBottom: 7,
              display: 'flex',
              alignItems: 'center',
              gap: 5,
            }}
          >
            <span
              style={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                background: color,
                boxShadow: `0 0 6px ${alpha(color, 0.8)}`,
                display: 'inline-block',
                flexShrink: 0,
              }}
            />
            {option.label}
          </div>
        );
        return (
          <motion.div
            key={option.name}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ...EASE_OUT_CUBIC, delay: i * 0.05 }}
          >
            {fieldLabel}
            {option.type === 'date' ? (
              <DateFilterInput
                name={option.name}
                value={filters[option.name] || ''}
                label={option.label}
                accentColor={color}
                onChange={(name, val) => onFilterChange({ ...filters, [name]: val })}
              />
            ) : option.type === 'number' ? (
              <NumberFilterInput
                name={option.name}
                value={filters[option.name] ?? ''}
                accentColor={color}
                min={option.min}
                max={option.max}
                step={option.step}
                placeholder={option.placeholder || `Any ${option.label.toLowerCase()}`}
                onChange={(name, val) => onFilterChange({ ...filters, [name]: val })}
              />
            ) : (
              <CustomSelect
                name={option.name}
                value={filters[option.name] || ''}
                options={option.options || []}
                onChange={(name, val) => onFilterChange({ ...filters, [name]: val })}
                placeholder={`Any ${option.label.toLowerCase()}`}
                accentColor={color}
                searchable={option.searchable ?? (option.options?.length >= 4)}
              />
            )}
          </motion.div>
        );
      })}
    </div>

    {customFilters?.length > 0 && (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 160px), 1fr))', gap: 16, marginTop: 16 }}>
        {customFilters}
      </div>
    )}

    {/* Active chips */}
    <AnimatePresence>
      {activeCount > 0 && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.22, ease: 'easeInOut' }}
          style={{ overflow: 'hidden' }}
        >
          <div
            style={{
              marginTop: 16,
              paddingTop: 14,
              borderTop: `1px solid ${alpha(ACCENT_2, 0.15)}`,
              display: 'flex',
              flexWrap: 'wrap',
              gap: 8,
              alignItems: 'center',
            }}
          >
            <span style={{ fontSize: '0.7rem', color: alpha(TEXT_SECONDARY, 0.5), fontWeight: 600, marginRight: 2 }}>
              Active:
            </span>
            <AnimatePresence mode="popLayout">
              {Object.entries(filters).map(([key, value], i) => {
                if (!isActiveFilterValue(value)) return null;
                const option = filterOptions.find(o => o.name === key);
                const sel = option?.options?.find(o => String(o.value) === String(value));
                const color = ACCENT_CYCLE[filterOptions.findIndex(o => o.name === key) % ACCENT_CYCLE.length];
                return (
                  <GlowChip
                    key={key}
                    label={`${option?.label}: ${sel?.label || value}`}
                    color={color}
                    onDelete={() => onFilterChange({ ...filters, [key]: '' })}
                  />
                );
              })}
            </AnimatePresence>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  </motion.div>
);

// ─── MAIN FILTER BAR ─────────────────────────────────────────────
const FilterBar = ({
  searchTerm,
  onSearchChange,
  filters,
  onFilterChange,
  onClearFilters,
  filterOptions = [],
  showFilters,
  onToggleFilters,
  searchPlaceholder = 'Search...',
  customFilters,
}) => {
  const activeCount = countActiveFilters(filters);
  const hasFilterUi = filterOptions.length > 0 || (customFilters?.length > 0);

  return (
    <div style={{ width: '100%', fontFamily: "'Inter', 'SF Pro Display', system-ui, sans-serif" }}>
      <div style={{
        display: 'flex',
        gap: 10,
        alignItems: 'stretch',
        flexWrap: 'wrap',
      }}>
        <div style={{ flex: '1 1 320px', minWidth: 0, display: 'flex' }}>
          <SearchInput
            value={searchTerm}
            onChange={onSearchChange}
            placeholder={searchPlaceholder}
          />
        </div>
        {hasFilterUi && (
          <div style={{ flex: '0 0 auto', display: 'flex', alignItems: 'stretch' }}>
            <FilterToggle
              onClick={onToggleFilters}
              active={showFilters}
              activeCount={activeCount}
            />
          </div>
        )}
      </div>

      <AnimatePresence>
        {showFilters && hasFilterUi && (
          <FilterPanel
            filterOptions={filterOptions}
            filters={filters}
            onFilterChange={onFilterChange}
            onClearFilters={onClearFilters}
            activeCount={activeCount}
            customFilters={customFilters}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default FilterBar;
